import { FC, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Button, Flex } from 'antd';
import {
  DetailLayout,
  ObjectHeader,
  ObjectType,
  Padding,
  Spinner,
  useDataSubjectQuery,
  ContentSegmentTotalEntries,
  useDataSubjectHistoryQuery,
  ContentSegments,
  LockedBy,
  ActionButton,
  isLocked,
  useShowLockData,
  useCheckLockAndNavigate,
  useQueryParams,
  useDataSubjectUpdateFromMyr,
  MyrAlerts,
  useHasPermission,
  PermissionCode,
  useDataSubjectInputsQuery,
  ISODate,
  loadViewState,
  InfoIconUrl,
} from '@atlas-frontend/sdk';
import { OpenDirectoryButton } from '../../common/OpenDirectoryButton';
import { path, type UiPathName } from '../../paths';
import { UiLink } from '../../paths/UiLink';
import { useNavigateTo } from '../../paths/useNavigateTo';
import { DataSubjectBreadcrumbs } from './DataSubjectBreadcrumbs';
import { DataSubjectDescription } from './DataSubjectDescription';
import { NotFound } from '../NotFound';
import { DataSubjectDetailsContextProvider } from './DataSubjectDetailsContext';
import { DataSubjectHistoryTable } from './DataSubjectHistoryTable';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './DataSubjectDetails.translations.json';
import { DataSubjectCommentsViewTable } from './table/DataSubjectCommentsViewTable';
import { DataSubjectInputsTable } from './DataSubjectInputsTable';

const { addTranslations, useTranslation } = i18nNsFactory(
  'data-subject-details',
);
addTranslations(translations);

export const DataSubjectDetails: FC = () => {
  const { t } = useTranslation();
  const params = useParams<{ id: string }>();
  const dataSubjectId = Number(params.id);

  const { data: dataSubject, isLoading } = useDataSubjectQuery({
    id: dataSubjectId,
  });

  const {
    data: { content: dataSubjectHistory } = {},
    isLoading: isLoadingHistory,
  } = useDataSubjectHistoryQuery(
    { id: dataSubject?.id || 0, page: 0, size: 10_000 },
    { enabled: Boolean(dataSubject?.id) },
  );

  const [inputValueDate, setInputValueDate] = useState<ISODate | null>(null);
  const {
    data: { content: dataSubjectInputs } = {},
    isFetching: isLoadingInputs,
  } = useDataSubjectInputsQuery(
    {
      id: dataSubject?.id || 0,
      valueDate: inputValueDate || '0000-00-00',
      page: 0,
      size: 10_000,
    },
    { enabled: Boolean(dataSubject?.id) && Boolean(inputValueDate) },
  );

  const [{ segment }] = useQueryParams();

  const { lockData, refetchLockData } = useShowLockData(
    ObjectType.DataSubject,
    dataSubject?.id,
  );
  const navigateToDataSubjectEdit = useNavigateTo(path.dataSubjectEdit, {
    id: dataSubject?.id || 0,
  });
  const checkLockAndNavigateToEdit = useCheckLockAndNavigate(
    refetchLockData,
    lockData,
    () => navigateToDataSubjectEdit({ segment }),
  );

  const segments = useMemo(
    () => [
      {
        value: 'inputs',
        label: (
          <Flex gap="small" align="center">
            {t('segment.inputs')} <Badge count={dataSubjectInputs?.length} />
          </Flex>
        ),
        count: dataSubjectInputs?.length,
        children: dataSubject?.id && (
          <DataSubjectInputsTable
            data={dataSubjectInputs || []}
            dataSubjectCode={dataSubject.code}
            valueDate={inputValueDate}
            onChangeValueDate={setInputValueDate}
            isLoading={isLoadingInputs}
          />
        ),
      },
      {
        value: 'history',
        label: (
          <Flex gap="small" align="center">
            {t('segment.history')} <Badge count={dataSubjectHistory?.length} />
          </Flex>
        ),
        count: dataSubjectHistory?.length,
        children: dataSubjectHistory && (
          <DataSubjectHistoryTable
            data={dataSubjectHistory}
            isLoading={isLoadingHistory}
          />
        ),
      },
      {
        value: 'comments',
        label: (
          <Flex gap="small" align="center">
            {t('segment.comments')}{' '}
            <Badge count={dataSubject?.comments.length} />
          </Flex>
        ),
        count: dataSubject?.comments.length,
        children: dataSubject && (
          <DataSubjectCommentsViewTable comments={dataSubject.comments} />
        ),
      },
    ],
    [
      t,
      dataSubject,
      dataSubjectInputs,
      inputValueDate,
      isLoadingInputs,
      dataSubjectHistory,
      isLoadingHistory,
    ],
  );

  const {
    refetch: myrRefetch,
    isFetching: isMyrFetching,
    isError: isMyrError,
    isSuccess: isMyrSuccess,
  } = useDataSubjectUpdateFromMyr(dataSubject?.code, {
    enabled: false,
  });

  const handleUpdateFromMyr = useCallback(() => myrRefetch(), [myrRefetch]);

  const hasPermissionToUpdateFromMyr = useHasPermission(
    PermissionCode.SUBJECT_SINGLE_LOAD,
  );
  const hasPermissionToManage = useHasPermission(PermissionCode.SUBJECT_MANAGE);

  const openDirectoryButtonOptions = useMemo(() => {
    const options = [];

    options.push({
      id: 'dataSubjectDirectory',
      path: path.dataSubjectDirectory,
      label: t('button.dataSubjectDirectory'),
    });

    const viewState = loadViewState<UiPathName>('inputDataDetails');
    if (viewState) {
      options.push({
        id: 'inputDataDetails',
        path: path.inputDataDetails,
        params: {
          descriptionId: viewState.descriptionId,
          date: viewState.date,
          subject: viewState.subject,
          groupId: viewState.groupId,
        },
        label: t('button.inputDataDetails'),
      });
    }

    return options;
  }, [t]);

  if (isLoading || isLoadingHistory) {
    return <Spinner />;
  }

  if (!dataSubject) {
    return <NotFound />;
  }

  return (
    <DataSubjectDetailsContextProvider>
      <DetailLayout
        infoIconUrl={InfoIconUrl.DATA_SUBJECT_DETAILS}
        breadCrumb={<DataSubjectBreadcrumbs data={{ dataSubject }} />}
        headerConfig={{
          actions: (
            <>
              {hasPermissionToUpdateFromMyr && (
                <Button
                  id="update-from-myr"
                  type="primary"
                  onClick={handleUpdateFromMyr}
                  loading={isMyrFetching}
                >
                  {t('updateFromMyr')}
                </Button>
              )}
              <OpenDirectoryButton options={openDirectoryButtonOptions} />
              {hasPermissionToManage && (
                <UiLink
                  id="mainAction-link"
                  path={path.dataSubjectEdit}
                  params={
                    segment
                      ? { id: dataSubject.id, segment }
                      : { id: dataSubject.id }
                  }
                  onClick={(event) => {
                    if (!event.metaKey && !event.ctrlKey) {
                      checkLockAndNavigateToEdit();
                    }
                  }}
                >
                  {({ type, ...rest }) => (
                    <ActionButton
                      disabled={isLocked(lockData)}
                      layoutType="detail"
                      type="mainAction"
                      {...rest}
                    />
                  )}
                </UiLink>
              )}
            </>
          ),
        }}
        side={{
          header: (
            <Padding>
              <ObjectHeader
                upperText={dataSubject.code}
                lowerText={dataSubject.name}
              />
            </Padding>
          ),
          content: <DataSubjectDescription data={dataSubject} />,
          footer: (
            <LockedBy
              id={dataSubject.id}
              entity={dataSubject}
              objectType={ObjectType.DataSubject}
            />
          ),
        }}
        alert={
          <MyrAlerts
            isFetching={isMyrFetching}
            isSuccess={isMyrSuccess}
            isError={isMyrError}
          />
        }
        footerContent={<ContentSegmentTotalEntries segments={segments} />}
      >
        <Padding>
          <ContentSegments segments={segments} />
        </Padding>
      </DetailLayout>
    </DataSubjectDetailsContextProvider>
  );
};
